import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { fetchUsersCheckInBlockers } from "src/models/user/user";
import { CheckInBlockerDialogProps } from "src/shared/CheckinBlocker/CheckInBlockerDialog";
import {
  CheckinBlockerOverrideContext,
  CheckinBlockerUser,
} from "src/shared/CheckinBlocker/_types";
import { Identifier } from "src/types/utilityTypes";

const PAYMENT_RESOLVED_CLOSE_DELAY_MS = 500;

function getUnresolvedUsers(users: CheckinBlockerUser[]) {
  return users
    .map((user) => ({
      ...user,
      blockers: user.blockers.filter((blocker) => !blocker.resolved),
    }))
    .filter((user) => user.blockers.length > 0);
}

function getOverrideResolvedUsers(users: CheckinBlockerUser[]) {
  return users
    .map((user) => ({
      ...user,
      blockers: user.blockers.filter(
        (blocker) => blocker.resolvedBy === 'override'
      ),
    }))
    .filter((user) => user.blockers.length > 0);
}

function getCloseResultUsers(users: CheckinBlockerUser[]) {
  const unresolvedUsers = getUnresolvedUsers(users);
  if (unresolvedUsers.length > 0) return unresolvedUsers;

  // Manual overrides already performed the requested action inline. Return a
  // non-empty result so callers do not repeat the original bulk check-in flow.
  return getOverrideResolvedUsers(users);
}

type UseCheckInBlockerOptions = Partial<
  Pick<CheckInBlockerDialogProps, 'title' | 'subtitle'> & {
    onOverride?: (context: CheckinBlockerOverrideContext) => void | Promise<void>;
    showUserDetails: boolean | ((users: CheckinBlockerUser[]) => boolean);
  }
>;

export interface CheckBlockersInput {
  userIds?: Identifier[];
  barcodes?: string[];
}

export type UseCheckInBlockerReturn = [
  CheckInBlockerDialogProps,
  (input: CheckBlockersInput) => Promise<CheckinBlockerUser[]>,
  (users: CheckinBlockerUser[]) => void,
  boolean,
];

export function useCheckInBlocker(
  initialOptions: UseCheckInBlockerOptions = {}
): UseCheckInBlockerReturn {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<CheckinBlockerUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: CheckinBlockerUser[]) => void) | null
  >(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkBlockersRef = useRef<
    ((input: CheckBlockersInput) => Promise<CheckinBlockerUser[]>) | null
  >(null);
  const initialOptionsRef = useRef(initialOptions);
  const resolvePromiseRef = useRef(resolvePromise);
  const usersRef = useRef(users);

  initialOptionsRef.current = initialOptions;
  resolvePromiseRef.current = resolvePromise;
  usersRef.current = users;

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const openWithUsers = (newUsers: CheckinBlockerUser[]) => {
    setUsers(newUsers);
    setOpen(true);
  };

  if (!checkBlockersRef.current) {
    checkBlockersRef.current = async ({
      userIds = [],
      barcodes = [],
    }: CheckBlockersInput): Promise<CheckinBlockerUser[]> => {
      if (userIds.length === 0 && barcodes.length === 0) return [];

      setIsLoading(true);
      const result = await queryClient.fetchQuery<CheckinBlockerUser[]>({
        queryKey: ['checkinBlockers', userIds, barcodes],
        queryFn: () => fetchUsersCheckInBlockers({ userIds, barcodes }),
      });
      setIsLoading(false);

      if (result.length === 0) return [];

      setUsers(result);
      setOpen(true);

      return new Promise((resolve) => {
        setResolvePromise(() => resolve);
      });
    };
  }

  const handleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setOpen(false);
    const promiseResolver = resolvePromiseRef.current;
    if (promiseResolver) {
      promiseResolver(getCloseResultUsers(usersRef.current));
      setResolvePromise(null);
    }
  };

  const handleBillingResolved = (userId: string) => {
    setUsers((currentUsers) => {
      const nextUsers = currentUsers.map((user) => {
        if (String(user.id) !== String(userId)) return user;

        return {
          ...user,
          blockers: user.blockers.map((blocker) =>
            blocker.type === 'billing_blocked'
              ? { ...blocker, resolved: true, resolvedBy: 'payment' as const }
              : blocker
          ),
        };
      });

      if (getUnresolvedUsers(nextUsers).length === 0) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }

        closeTimerRef.current = setTimeout(() => {
          setOpen(false);
          const promiseResolver = resolvePromiseRef.current;
          if (promiseResolver) {
            promiseResolver(getCloseResultUsers(nextUsers));
            setResolvePromise(null);
          }
          closeTimerRef.current = null;
        }, PAYMENT_RESOLVED_CLOSE_DELAY_MS);
      }

      return nextUsers;
    });
  };

  const handleOverride = async (context: CheckinBlockerOverrideContext) => {
    await initialOptionsRef.current.onOverride?.(context);

    if (context.scope === 'all') return;

    const userId = context.user?.id;
    if (!userId) return;

    setUsers((currentUsers) => {
      const nextUsers = currentUsers.map((user) => {
        if (String(user.id) !== String(userId)) return user;

        return {
          ...user,
          blockers: user.blockers.map((blocker) =>
            context.scope === 'user' || blocker.type === context.blocker?.type
              ? { ...blocker, resolved: true, resolvedBy: 'override' as const }
              : blocker
          ),
        };
      });

      if (getUnresolvedUsers(nextUsers).length === 0) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }

        closeTimerRef.current = setTimeout(() => {
          setOpen(false);
          const promiseResolver = resolvePromiseRef.current;
          if (promiseResolver) {
            promiseResolver(getCloseResultUsers(nextUsers));
            setResolvePromise(null);
          }
          closeTimerRef.current = null;
        }, PAYMENT_RESOLVED_CLOSE_DELAY_MS);
      }

      return nextUsers;
    });
  };

  const resolvedShowUserDetails =
    typeof initialOptions.showUserDetails === 'function'
      ? initialOptions.showUserDetails(users)
      : initialOptions.showUserDetails;

  const dialogProps: CheckInBlockerDialogProps = {
    open,
    onClose: handleClose,
    title: initialOptions.title,
    subtitle: initialOptions.subtitle,
    showUserDetails: resolvedShowUserDetails,
    onOverride: initialOptions.onOverride ? handleOverride : undefined,
    onBillingResolved: handleBillingResolved,
    users,
  };

  return [dialogProps, checkBlockersRef.current, openWithUsers, isLoading];
}
